import React, { useEffect, useState } from 'react';
import { Button, Card, Col, DatePicker, Divider, Form, InputNumber, Row, Select, Spin, Table, Tabs, Upload } from 'antd';
import { FileExcelFilled, UploadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import moment, { Moment } from 'moment';
import { Excel } from 'antd-table-saveas-excel';
import { SkuService } from '@gtpl/shared-services/masters';
import { RmCountPriceListService } from '@gtpl/shared-services/raw-material-procurement';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { SkuPriceCreateRequestDto, SkuPriceItemDto } from '@gtpl/shared-models/raw-material-procurement';
import { useHistory } from 'react-router-dom';

interface SkuOption {
  skuCodeId: number;
  shortCode?: string;
  variantCode?: string;
  isActive?: boolean;
}

interface ExcelPreviewRow {
  key: number;
  rowNumber: number;
  skuRaw: string;
  skuId?: number;
  price?: number;
  error?: string;
}

export function SkuPriceForm() {
  const [form] = Form.useForm();
  const history = useHistory();
  const skuService = new SkuService();
  const priceService = new RmCountPriceListService();
  const [skuOptions, setSkuOptions] = useState<SkuOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [maxEffectiveDate, setMaxEffectiveDate] = useState<Moment | null>(null);
  const [excelRows, setExcelRows] = useState<ExcelPreviewRow[]>([]);
  const [bulkEffectiveDate, setBulkEffectiveDate] = useState<Moment | null>(moment());
  const createdUser = localStorage.getItem('createdUser') || 'Admin';
  const [currentSkuPrices, setCurrentSkuPrices] = useState<any[]>([]);
  useEffect(() => {
    loadSkus();
    loadCurrentSkuPrices();

    form.setFieldsValue({
        effectiveFromDate: moment(),
        items: [{}],
    });
}, []);

  const loadSkus = async () => {
    try {
      const res = await skuService.getActiveSkuCodes();
      if (res.status) {
        setSkuOptions(res.data || []);
      }
    } catch (err) {
      AlertMessages.getErrorMessage('Failed to load Product master data');
    }
  };

  const loadCurrentSkuPrices = async () => {
    try {
        const res = await priceService.getCurrentSkuPrices({});
        if (res.status) {
            setCurrentSkuPrices(res.data || []);
        }
    } catch {
        AlertMessages.getErrorMessage('Failed to load SKU prices');
    }
};

  const getSkuLabel = (sku: SkuOption) => sku.shortCode || sku.variantCode || String(sku.skuCodeId);

  const findSkuByValue = (value: string | number): SkuOption | undefined => {
    const normalized = String(value).trim();
    if (!normalized) return undefined;
    if (!isNaN(Number(normalized))) {
      return skuOptions.find((s) => s.skuCodeId === Number(normalized));
    }
    return skuOptions.find(
      (s) =>
        String(s.shortCode || '').toLowerCase() === normalized.toLowerCase() ||
        String(s.variantCode || '').toLowerCase() === normalized.toLowerCase(),
    );
  };

  const handleSkuChange = async (skuId: number) => {
    form.setFieldsValue({ skuId });
    try {
      const items = form.getFieldValue('items') || [];
      const res = await priceService.getSkuMaxEffectiveDate(skuId);
      if (res.status && res.data?.maxEffectiveDate) {
        setMaxEffectiveDate(moment(res.data.maxEffectiveDate));
      } else {
        setMaxEffectiveDate(null);
      }
    } catch {
      setMaxEffectiveDate(null);
    }
  };

  const disabledDate = (current: Moment) => {
  if (!current) return false;

  if (current.isAfter(moment(), 'day')) {
    return true;
  }

  if (maxEffectiveDate) {
    return current.isSameOrBefore(maxEffectiveDate, 'day');
  }

  return false;
};

  const resetSingleForm = () => {
    form.resetFields();

    form.setFieldsValue({
      effectiveFromDate: moment(),
      items: [{}],
    });

    setMaxEffectiveDate(null);
  };

  const savePrices = async (items: SkuPriceItemDto[], effectiveFrom: Date) => {
    const payload: SkuPriceCreateRequestDto = {
      items,
      effectiveFrom,
      createdUser,
    };
    setLoading(true);
    try {
      const res = await priceService.createSkuPrice(payload);
      if (res.status) {
        AlertMessages.getSuccessMessage(res.internalMessage || 'Products price saved successfully');
        resetSingleForm();
        setExcelRows([]);
        setBulkEffectiveDate(moment());
      } else {
        AlertMessages.getErrorMessage(res.internalMessage || 'Failed to save Products price');
      }
    } catch (err: any) {
      AlertMessages.getErrorMessage(err?.message || 'Failed to save Products price');
    } finally {
      setLoading(false);
    }
  };

  const onSingleSubmit = async (values: any) => {
    const items: SkuPriceItemDto[] = values.items.map((item: any) => ({
      skuId: item.skuId,
      price: Number(item.price),
    }));

    const skuSet = new Set<number>();

    for (const item of items) {
      if (skuSet.has(item.skuId)) {
        AlertMessages.getErrorMessage('Duplicate Products are not allowed.');
        return;
      }
      skuSet.add(item.skuId);

      if (!item.price || item.price <= 0) {
        AlertMessages.getErrorMessage('Price must be greater than zero.');
        return;
      }
    }

    await savePrices(items, values.effectiveFromDate.toDate());
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = XLSX.utils.sheet_to_json<Record<string, any>>(workbook.Sheets[sheetName], { defval: '' });

      if (sheet.length > 1000) {
        AlertMessages.getErrorMessage('Maximum 1000 rows allowed per upload');
        return;
      }

      const seenSkus = new Map<string, number>();
      const parsed: ExcelPreviewRow[] = sheet.map((row, index) => {
        const normalized: Record<string, any> = {};
        Object.keys(row).forEach((key) => {
          const newKey = key.replace(/\s/g, '').replace(/[\(\)\.]/g, '').replace(/-/g, '').toLowerCase();
          normalized[newKey] = row[key];
        });

        const excelRow = index + 2;
        const rowNumber = index + 1;
        const skuRaw = String(
          normalized.productsku ??
          normalized.product ??
          normalized.sku ??
          normalized.skuid ??
          normalized.skucode ??
          ''
        ).trim();
        const priceRaw = normalized.price;
        const price = priceRaw === '' || priceRaw === undefined ? NaN : Number(priceRaw);
        let error = '';

        if (!skuRaw) {
          error = 'Product is mandatory';
        } else if (priceRaw === '' || priceRaw === undefined || isNaN(price)) {
          error = 'Price is mandatory and must be numeric';
        } else if (!price || price <= 0) {
          error = 'Price must be greater than zero';
        } else {
          const skuMatch = findSkuByValue(skuRaw);
          if (!skuMatch) {
            error = `Product ${skuRaw} does not exist`;
          } else if (skuMatch.isActive === false) {
            error = `Product ${getSkuLabel(skuMatch)} is inactive`;
          } else {
            const skuKey = String(skuMatch.skuCodeId);
            if (seenSkus.has(skuKey)) {
              error = `Duplicate Product ${getSkuLabel(skuMatch)} (also in row ${seenSkus.get(skuKey)})`;
            } else {
              seenSkus.set(skuKey, excelRow);
            }
          }
        }

        const skuMatch = !error ? findSkuByValue(skuRaw) : undefined;

        return {
          key: index,
          rowNumber,
          skuRaw,
          skuId: skuMatch?.skuCodeId,
          price: isNaN(price) ? undefined : price,
          error: error || undefined,
        };
      });

      setExcelRows(parsed);
    };
    reader.readAsArrayBuffer(file);
  };

  const exportSampleExcel = () => {
    const excel = new Excel();
    excel
      .addSheet('Product Price Sample')
      .addColumns([
        { title: 'Product SKU', dataIndex: 'sku', width: 120 },
        { title: 'Price', dataIndex: 'price', width: 100 },
      ])
      .addDataSource(
  currentSkuPrices.map((item) => ({
    sku: item.skuName,
    price: Number(item.price),
  }))
)
      .saveAs('product-price-sample.xlsx');
  };

  const submitBulkUpload = async () => {
    if (!bulkEffectiveDate) {
      AlertMessages.getErrorMessage('Please select effective date for bulk upload');
      return;
    }
    if (!excelRows.length) {
      AlertMessages.getErrorMessage('Please upload a valid Excel file first');
      return;
    }

    const invalidRows = excelRows.filter((row) => row.error);
    if (invalidRows.length) {
      AlertMessages.getErrorMessage(
        invalidRows.map((r) => `Row ${r.rowNumber}: ${r.error}`).join('; '),
      );
      return;
    }
    const skuSet = new Set<number>();

    for (const row of excelRows) {

      if (!row.skuId) {
        AlertMessages.getErrorMessage(`Invalid Product in Row ${row.rowNumber}`);
        return;
      }

      if (skuSet.has(row.skuId)) {
        AlertMessages.getErrorMessage(
          `Duplicate Product found in Row ${row.rowNumber}`
        );
        return;
      }

      skuSet.add(row.skuId);

      if (!row.price || row.price <= 0) {
        AlertMessages.getErrorMessage(
          `Price must be greater than zero in Row ${row.rowNumber}`
        );
        return;
      }

    }

    const items: SkuPriceItemDto[] = excelRows.map((row) => ({
      skuId: row.skuId!,
      price: row.price!,
    }));

    await savePrices(items, bulkEffectiveDate.toDate());
  };

  const previewColumns = [
    { title: 'Row', dataIndex: 'rowNumber', width: 70 },
    { title: 'Product SKU', dataIndex: 'skuRaw', width: 160 },
    {
      title: 'Price',
      dataIndex: 'price',
      width: 100,
      render: (val: number) => (val !== undefined ? val : '-'),
    },
    
  ];

  return (
    <Spin spinning={loading}>
      <Card
        title={<span style={{ color: 'white' }}>Product Sku Rates</span>}
style={{ margin: 20 }}
      headStyle={{
        backgroundColor: '#69c0ff',
        border: 0,
        textAlign: 'center',
      }}        extra={
          <Button
            type="primary"
            onClick={() => history.push('/sku-price-view')}
            
          >
            View
        </Button>
        }
      >
        <Tabs defaultActiveKey="single">
          <Tabs.TabPane tab="Single Entry" key="single">
            <Form
              form={form}
              layout="vertical"
              onFinish={onSingleSubmit}
            >
              <Row gutter={16}>
                <Col span={5}>
                  <Form.Item
                    name="effectiveFromDate"
                    label="Effective Date"
                    rules={[
                      {
                        required: true,
                        message: 'Effective date is required',
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format="DD-MMM-YYYY"
                      disabledDate={disabledDate}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.List name="items">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name }, index) => (
                      <Row
                        key={key}
                        gutter={12}
                        align="top"
                        style={{ marginRight: 20, marginBottom: 8 }}
                      >
                        <Col span={6}>
                          <Form.Item
                            name={[name, 'skuId']}
                            label={index === 0 ? 'Product' : ''}
                            rules={[
                              {
                                required: true,
                                message: 'Product is required',
                              },
                            ]}
                          >
                            <Select
                              showSearch
                              placeholder="Select Product"
                              optionFilterProp="children"
                              onChange={handleSkuChange}
                            >
                              {skuOptions.map((sku) => (
                                <Select.Option
                                  key={sku.skuCodeId}
                                  value={sku.skuCodeId}
                                >
                                  {getSkuLabel(sku)}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col span={6}>
                          <Form.Item
                            name={[name, 'price']}
                            label={index === 0 ? 'Price' : ''}
                            rules={[
                              {
                                required: true,
                                message: 'Price is required',
                              },
                              {
                                validator: (_, value) =>
                                  value > 0
                                    ? Promise.resolve()
                                    : Promise.reject(
                                      new Error('Price must be greater than zero'),
                                    ),
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              min={0.01}
                              precision={2}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={2}>
                          <Form.Item
                            label={index === 0 ? ' ' : ''}
                            style={{ marginBottom: 24 }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 8,
                                }}
                              >
                                {fields.length > 1 && (
                                  <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => remove(name)}
                                  />
                                )}

                                {index === fields.length - 1 && (
                                  <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => add()}
                                  />
                                )}
                              </div>
                            </div>
                          </Form.Item>

                        </Col>
                      </Row>
                    ))}
                  </>
                )}
              </Form.List>

              {maxEffectiveDate && (
                <p style={{ color: '#888' }}>
                  Latest maintained effective date for selected Product:{' '}
                  {maxEffectiveDate.format('DD-MMM-YYYY')}
                </p>
              )}

              <Row justify="end" gutter={8}>
                <Col>
                  <Button onClick={resetSingleForm}>
                    Reset
                </Button>
                </Col>

                <Col>
                  <Button
                    type="primary"
                    htmlType="submit"
                  >
                    Save
                </Button>
                </Col>
              </Row>
            </Form>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Bulk Upload" key="bulk">
            <Row
              gutter={16}
              align="middle"
              style={{ marginBottom: 16 }}
            >
              <Col xs={24} md={8}>
                <Form.Item
                  label="Effective Date"
                  required
                >
                  <DatePicker
                  style={{ width: '100%' }}
                  format="DD-MMM-YYYY"
                  value={bulkEffectiveDate}
                  onChange={(val) => setBulkEffectiveDate(val)}
                  disabledDate={(current) => current && current.isAfter(moment(), 'day')}
                />
                </Form.Item>
              </Col>



              <Col xs={24} md={8}>
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    parseExcelFile(file);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>
                    Upload Excel
                </Button>
                </Upload>
              </Col>

              <Col xs={24} md={8}>
                <Button
                  icon={<FileExcelFilled />}
                  onClick={exportSampleExcel}
                >
                  Download Sample Excel
              </Button>
              </Col>
            </Row>

            

            {excelRows.length > 0 && (
              <>
                <Divider orientation="left">
                  Uploaded Prices
              </Divider>

                <Table
                  columns={previewColumns}
                  dataSource={excelRows}
                  pagination={{ pageSize: 10 }}
                  size="small"
                  scroll={{ x: 'max-content' }}
                />

                <Row
                  justify="end"
                  style={{ marginTop: 16 }}
                >
                  <Button
                    type="primary"
                    onClick={submitBulkUpload}
                  >
                    Submit
                </Button>
                </Row>
              </>
            )}
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </Spin>
  );
}

export default SkuPriceForm;
